import type { ReactNode } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { usePreloadedQuery, useQueryLoader, useRelayEnvironment } from 'react-relay';
import type { PreloadedQuery } from 'react-relay';
import { createOperationDescriptor, getRequest } from 'relay-runtime';
import type { GraphQLResponse, Subscription } from 'relay-runtime';

import {
	ContentUnifiedQueryArtifact,
	type ContentUnifiedQuery,
} from '@confluence/content-unified-query/entry-points/ContentUnifiedQuery.ts';
import type { ContentBlogUnifiedQueryType } from '@confluence/content-unified-query';
import { RelaySuspense } from '@confluence/query-preloader-tools/entry-points/RelaySuspense.ts';
import {
	RelayError,
	isRelayError,
	isRelayGraphQLError,
	type RelayGraphQLError,
} from '@confluence/relay-errors/entry-points/RelayError.ts';

export type DocumentQueryResult = {
	data?: ContentUnifiedQuery['response'] | ContentBlogUnifiedQueryType;
	loading?: boolean;
	error?: RelayError;
	refetch: () => Promise<{ publishedVersion?: number | null }>;
};

type Props = {
	variables: ContentUnifiedQuery['variables'];
	children: (result: DocumentQueryResult) => ReactNode;
};

export class DocumentQueryCancelledError extends Error {
	constructor() {
		super('Document query unmounted during reload');
	}
}

type PendingReload = {
	promise: ReturnType<DocumentQueryResult['refetch']>;
	resolve: (result: { publishedVersion?: number | null }) => void;
	reject: (error: Error) => void;
	subscription?: Subscription;
};

function responseErrors(response: GraphQLResponse): ReadonlyArray<RelayGraphQLError> {
	const responses = Array.isArray(response) ? response : [response];
	return responses.flatMap((payload) => payload.errors ?? []);
}

/** Only the response version is needed by quick reload; do not substitute cached data. */
function responseVersion(response: GraphQLResponse): number | undefined {
	for (const payload of Array.isArray(response) ? response : [response]) {
		const content = payload.data?.content;
		if (
			!content ||
			typeof content !== 'object' ||
			!('nodes' in content) ||
			!Array.isArray(content.nodes)
		)
			continue;
		const version = content.nodes[0]?.version;
		if (version && typeof version.number === 'number') return version.number;
	}
	return undefined;
}

function requestError(error: Error): RelayError {
	if (isRelayError(error)) return error;
	// Relay puts errors-only GraphQL responses on the thrown error's source.
	if (
		'source' in error &&
		error.source &&
		typeof error.source === 'object' &&
		'errors' in error.source &&
		Array.isArray(error.source.errors)
	) {
		const graphQLErrors = error.source.errors.filter(isRelayGraphQLError);
		if (graphQLErrors.length) return new RelayError({ graphQLErrors });
	}
	return new RelayError({ networkError: error });
}

/** Owns only runtime references. The route preloader independently warms the same Relay store.
 * The caller keys this component by variables, so another document can never read an old reference.
 */
export function RuntimeDocumentQuery({ variables, children }: Props): React.JSX.Element | null {
	const environment = useRelayEnvironment();
	// The parent's variables key remounts us whenever any input changes.
	const [queryVariables] = useState(variables);
	const [queryRef, loadQuery] = useQueryLoader<ContentUnifiedQuery>(ContentUnifiedQueryArtifact);
	const [initialResult, setInitialResult] = useState<{
		queryRef: PreloadedQuery<ContentUnifiedQuery>;
		error?: RelayError;
		failed?: boolean;
	}>();
	const [reloadResult, setReloadResult] = useState<{ error?: RelayError }>();
	const pendingReload = useRef<PendingReload>();

	useEffect(() => {
		loadQuery(queryVariables, {
			fetchPolicy: 'store-or-network',
			networkCacheConfig: { metadata: { single: true } },
		});
	}, [loadQuery, queryVariables]);

	useEffect(() => {
		if (!queryRef) return;
		if (!queryRef.source) {
			setInitialResult({ queryRef });
			return;
		}
		// Observe the existing request, not a second fetch. Relay's normalized store does not
		// retain response-level GraphQL errors; capture them before rendering prerequisites.
		const errors: RelayGraphQLError[] = [];
		const subscription = queryRef.source.subscribe({
			next: (response) => {
				errors.push(...responseErrors(response));
			},
			error: (error: Error) =>
				setInitialResult({ queryRef, error: requestError(error), failed: true }),
			complete: () =>
				setInitialResult({
					queryRef,
					error: errors.length ? new RelayError({ graphQLErrors: errors }) : undefined,
				}),
		});
		return () => subscription.unsubscribe();
	}, [queryRef]);

	useEffect(
		() => () => {
			const pending = pendingReload.current;
			pendingReload.current = undefined;
			pending?.subscription?.unsubscribe();
			pending?.reject(new DocumentQueryCancelledError());
		},
		[],
	);

	const refetch = useCallback(() => {
		if (pendingReload.current) return pendingReload.current.promise;
		let resolve!: PendingReload['resolve'];
		let reject!: PendingReload['reject'];
		const promise: PendingReload['promise'] = new Promise((resolvePromise, rejectPromise) => {
			resolve = resolvePromise;
			reject = rejectPromise;
		});
		const pending: PendingReload = { promise, resolve, reject };
		pendingReload.current = pending;
		const errors: RelayGraphQLError[] = [];
		let publishedVersion: number | null | undefined;
		// Refresh the retained operation without replacing the reader/reference or remounting
		// the page. Use the response version, never a version another query wrote to the store.
		const operation = createOperationDescriptor(
			getRequest(ContentUnifiedQueryArtifact),
			queryVariables,
			{ force: true, metadata: { single: true } },
		);
		pending.subscription = environment.execute({ operation }).subscribe({
			next: (response) => {
				errors.push(...responseErrors(response));
				publishedVersion = responseVersion(response) ?? publishedVersion;
			},
			error: (error: Error) => {
				const failure = requestError(error);
				pendingReload.current = undefined;
				pending.reject(failure);
				setReloadResult({ error: failure });
			},
			complete: () => {
				if (initialResult?.failed) {
					// A failed source cannot be reused by usePreloadedQuery. The successful
					// refresh already populated the store, so recovery needs no second fetch.
					loadQuery(queryVariables, { fetchPolicy: 'store-only' });
				}
				pendingReload.current = undefined;
				pending.resolve({ publishedVersion });
				setReloadResult({
					error: errors.length ? new RelayError({ graphQLErrors: errors }) : undefined,
				});
			},
		});
		return promise;
	}, [environment, queryVariables, initialResult?.failed, loadQuery]);

	if (!queryRef || initialResult?.queryRef !== queryRef) return null;
	if (initialResult.failed) {
		return (
			<>
				{children({ loading: false, error: reloadResult?.error ?? initialResult.error, refetch })}
			</>
		);
	}
	return (
		<RelaySuspense fallback={null} interactionName="document-runtime-query">
			<DocumentReader
				queryRef={queryRef}
				error={reloadResult ? reloadResult.error : initialResult.error}
				refetch={refetch}
			>
				{children}
			</DocumentReader>
		</RelaySuspense>
	);
}

function DocumentReader({
	queryRef,
	children,
	error,
	refetch,
}: {
	queryRef: PreloadedQuery<ContentUnifiedQuery>;
	children: Props['children'];
	error?: RelayError;
	refetch: DocumentQueryResult['refetch'];
}) {
	const data = usePreloadedQuery<ContentUnifiedQuery>(ContentUnifiedQueryArtifact, queryRef);
	return <>{children({ data, error, loading: false, refetch })}</>;
}
